
import { NotificationConfig } from '@/types/notificationConfig';
import { BaseNotification } from '@/types/notifications';
import { toast } from 'sonner';
import { offlineNotificationService } from '@/utils/notifications/offline/OfflineNotificationService';
import { defaultToastConfig } from '@/utils/toastConfig';

export interface DeliveryOptions {
  to?: string; // email or phone number
  subject?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
  metadata?: Record<string, any>;
}

export class NotificationDeliveryService {
  // Check if notification should be sent based on config
  public shouldSendNotification(
    notification: BaseNotification, 
    config: NotificationConfig
  ): boolean {
    if (!config.enabled) return false;
    
    // Check if we've reached max escalation level
    if (
      config.escalationLevels !== undefined && 
      config.currentEscalationLevel !== undefined &&
      config.currentEscalationLevel >= config.escalationLevels
    ) {
      return false;
    }
    
    // Check if it's time to send a reminder based on reminderFrequency
    if (config.reminderFrequency !== 'none' && config.lastReminderSent) {
      const lastSent = new Date(config.lastReminderSent);
      const now = new Date();
      
      // For daily reminders, check if at least 24 hours have passed
      if (config.reminderFrequency === 'daily') {
        const hoursSinceLastReminder = (now.getTime() - lastSent.getTime()) / (1000 * 60 * 60);
        if (hoursSinceLastReminder < 24) return false;
      }
      
      // For weekly reminders, check if at least 7 days have passed
      if (config.reminderFrequency === 'weekly') {
        const daysSinceLastReminder = (now.getTime() - lastSent.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceLastReminder < 7) return false;
      }
    }
    
    return true;
  }
  
  // Send push notification (in-app)
  public async sendPushNotification(
    notification: BaseNotification,
    options?: DeliveryOptions
  ): Promise<boolean> {
    try {
      if (!navigator.onLine) {
        // Queue notification for later if offline
        await offlineNotificationService.queueNotification(notification);
        
        toast.info("You're offline. Notification will be delivered when you're back online.", {
          duration: defaultToastConfig.duration,
        });
        
        return true;
      }
      // In a real app, this would integrate with a push notification service
      // For now, we'll just show a toast message to simulate it
      console.log('PUSH NOTIFICATION:', notification.title, notification.message);
      
      toast.info(notification.title, {
        description: notification.message,
        duration: defaultToastConfig.duration,
      });
      
      return true;
    } catch (error) {
      console.error('Failed to send push notification:', error);
      return false;
    }
  }
  
  // Send email notification
  public async sendEmailNotification(
    notification: BaseNotification,
    recipient: string,
    options?: DeliveryOptions
  ): Promise<boolean> {
    try {
      // In a real app, this would integrate with an email service
      // For demonstration, we'll log it to console and show a toast
      console.log('EMAIL NOTIFICATION TO:', recipient);
      console.log('SUBJECT:', options?.subject || notification.title);
      console.log('BODY:', notification.message);
      
      toast.info(`Email would be sent to ${recipient}`, {
        description: `Subject: ${options?.subject || notification.title}`,
        duration: defaultToastConfig.duration,
      });
      
      return true;
    } catch (error) {
      console.error('Failed to send email notification:', error);
      return false;
    }
  }
  
  // Send SMS notification
  public async sendSmsNotification(
    notification: BaseNotification,
    phoneNumber: string,
    options?: DeliveryOptions
  ): Promise<boolean> {
    try {
      // In a real app, this would integrate with an SMS service
      // For demonstration, we'll log it to console and show a toast
      console.log('SMS NOTIFICATION TO:', phoneNumber);
      console.log('MESSAGE:', notification.message);
      
      toast.info(`SMS would be sent to ${phoneNumber}`, {
        description: notification.message,
        duration: defaultToastConfig.duration,
      });
      
      return true;
    } catch (error) {
      console.error('Failed to send SMS notification:', error);
      return false;
    }
  }
  
  // Send notification to all enabled channels
  public async sendToAllChannels(
    notification: BaseNotification,
    config: NotificationConfig,
    options: DeliveryOptions
  ): Promise<{success: boolean, channels: string[]}> {
    if (!this.shouldSendNotification(notification, config)) {
      return { success: false, channels: [] };
    }
    
    const successfulChannels: string[] = [];
    
    // Send push notification if enabled
    if (config.pushEnabled) {
      const pushSuccess = await this.sendPushNotification(notification, options);
      if (pushSuccess) successfulChannels.push('push');
    }
    
    // Send email notification if enabled and recipient provided
    if (config.emailEnabled && options.to && options.to.includes('@')) {
      const emailSuccess = await this.sendEmailNotification(notification, options.to, options);
      if (emailSuccess) successfulChannels.push('email');
    }
    
    // Send SMS notification if enabled and phone number provided
    if (config.smsEnabled && options.to && !options.to.includes('@')) {
      const smsSuccess = await this.sendSmsNotification(notification, options.to, options);
      if (smsSuccess) successfulChannels.push('sms');
    }
    
    // Update last reminder sent time
    const updatedConfig = { 
      ...config,
      lastReminderSent: new Date().toISOString(),
      // Increment escalation level if configured
      currentEscalationLevel: 
        config.currentEscalationLevel !== undefined 
          ? config.currentEscalationLevel + 1 
          : 1
    };
    
    // In a real implementation, we would save this updated config
    // This will be handled by the NotificationManager
    
    return {
      success: successfulChannels.length > 0,
      channels: successfulChannels
    };
  }
}
